import type { FC } from "react";
import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { isEmpty } from "lodash";

import { colorsPalettes } from "@similarweb/styles";
import { ShareBar, CountryDropdownButton, CountryDropdownItem } from "@similarweb/ui-components";

import { getPreferredCountry } from "app/pages/segments/utils/utils";
import { swSettings } from "app/common/services/swSettings";
import { DropdownReact } from "app/components/dropdown/dropdown-react";
import * as utils from "app/components/filters-bar/utils";
import { i18nFilter } from "app/filters/i18nFilter";
import { minVisitsAbbrFilter, percentageFilter } from "app/filters/ngFilters";
import { useSegmentWizardContext } from "app/pages/segments/redesigned-wizard/segment-wizard/SegmentWizardContextProvider";
import type { ISegmentsValidationSectionProps } from "app/pages/segments/redesigned-wizard/segment-wizard/types";

import {
    DataRow,
    DataWrapper,
    StyledShareBar,
    SegmentsValidationContainer,
    TitleRow,
} from "./segmentsValidationStyles";

const SEGMENT_SHARE_THRESHOLD = 0.01;
const SEGMENT_VISITS_THRESHOLD = 5000;

export const SegmentsValidationSection: FC<ISegmentsValidationSectionProps> = ({ segmentShare, segmentVisits }) => {
    const { selectedCountry, setSelectedCountry } = useSegmentWizardContext();
    const {
        params: { sid },
    } = useCurrentStateAndParams();

    const availableCountries = useMemo(() => {
        const currentSetting = swSettings.current;
        const allCountries = utils.getCountries(false, currentSetting).reduce((acc, item) => {
            if (item.permitted) {
                const { text, id } = item;
                acc.push({
                    text,
                    id,
                });
            }
            return acc;
        }, []);

        const defaultCountry = getPreferredCountry(allCountries, sid);

        setSelectedCountry(defaultCountry);
        return allCountries;
    }, []);

    const renderItem = (item, isSelected) => {
        const label = item.text || item.title;
        return (
            <CountryDropdownItem
                key={item.id}
                id={item.id}
                text={label}
                selected={isSelected}
                tooltipText={item.tooltipText}
                disabled={item.disabled}
            />
        );
    };

    const onCountryChange = (item) => {
        setSelectedCountry(item);
    };

    const buttonComponent = useMemo(() => {
        if (!isEmpty(selectedCountry)) {
            return (
                <CountryDropdownButton
                    key={selectedCountry.text}
                    countryId={selectedCountry.id}
                >
                    {selectedCountry.text}
                </CountryDropdownButton>
            );
        }
    }, [selectedCountry]);

    const isLoading = !segmentShare && !segmentVisits;
    const isLowShare = segmentShare < SEGMENT_SHARE_THRESHOLD;
    const isLowVisits = segmentVisits < SEGMENT_VISITS_THRESHOLD;
    const segmentShareNumber = isLowShare ? "<1%" : `${percentageFilter()(segmentShare, 0)}%`;

    return (
        <SegmentsValidationContainer>
            <DropdownReact
                items={availableCountries}
                hasSearch={true}
                buttonComponent={buttonComponent}
                renderItem={renderItem}
                onChange={onCountryChange}
                width={326}
                selected={selectedCountry?.id}
                titleColor={"#2a3e52"}
                titleSize={16}
                titleWeight={0}
            />
            <DataWrapper>
                <div>
                    <TitleRow>{i18nFilter()("workspaces.segment.wizard.verify.section.verify.share.label")}</TitleRow>
                    <DataRow isLow={isLowShare}>{isLoading ? "-" : segmentShareNumber}</DataRow>
                </div>
                <div>
                    <TitleRow>{i18nFilter()("workspaces.segment.wizard.verify.section.verify.visits.label")}</TitleRow>
                    <DataRow isLow={isLowVisits}>{isLoading ? "-" : minVisitsAbbrFilter()(segmentVisits)}</DataRow>
                </div>
            </DataWrapper>
            <StyledShareBar>
                <ShareBar
                    value={segmentShare}
                    hideValue={true}
                    hideChangeValue={true}
                    secondaryColor={colorsPalettes.carbon[100]}
                    primaryColor={isLowShare ? colorsPalettes.red["s100"] : colorsPalettes.blue["400"]}
                />
            </StyledShareBar>
        </SegmentsValidationContainer>
    );
};
